import { CSSProperties, FunctionComponent, HTMLProps, JSX, ReactNode } from 'react';
import { ActionMeta, components, FilterOptionOption, InputActionMeta } from 'react-select';

import { ReactSelectInstanceRefObject, TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import EditStateSelectWrapper from './edit-state-select-wrapper';
import EditStateWrapper from './edit-state-wrapper';
import { ChildrenProps } from './edit-state-wrapper.types';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { ContainerProps } from '@ui/container/container.types';
import { TextProps } from '@ui/text/text/text.types';

type TEditStateSelectProps = {
  ref?: ReactSelectInstanceRefObject;
  onChange?: (options: TReactSelectOption[], actionMeta: ActionMeta<unknown>) => void;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
  options?: TReactSelectOption[];
  defaultValue?: TReactSelectOption[];
  /** only pass in value if you want the component to be controlled */
  value?: TReactSelectOption[];
  onMenuClose?: (selectedOptions: TReactSelectOption[]) => void;
  placeholder?: ReactNode;
  Label?: FunctionComponent<ChildrenProps> | string;
  hideHeader?: boolean;
  disabled?: boolean;
  Components?: {
    UnfocusedCustomSelectedOptionComponent?: React.FC<{
      option: TReactSelectOption;
    }>;
  } & Partial<typeof components>;
  isLoading?: boolean;
  loadingMessage?: () => JSX.Element;
  isCreatable?: boolean;
  cannotClearOptions?: TReactSelectOption[];
  onCannotClearOptions?: (options: TReactSelectOption[]) => void;
  dynamicMenuPlacement?: boolean;
  dynamicMenuPlacementOffset?: number;
  isMulti?: boolean;
  onFocusWithinChange?: (isFocused: boolean) => void;
  recalculateViewableItems?: number;
  noOptionsMessage?: string;
  filterOptions?: ((option: FilterOptionOption<unknown>, inputValue: string) => boolean) | null;
  styles?: ReturnType<typeof reactSelectAdminStyles>;
  menuListMaxHeight?: CSSProperties['maxHeight'];
  unfocusedMultiValueContainerProps?: Partial<ContainerProps>;
  unfocusedMultiValueImageProps?: HTMLProps<HTMLImageElement>;
  unfocusedMultiValueTextProps?: Partial<TextProps>;
  unfocusedMultiValuePlusMoreContainerProps?: Partial<ContainerProps>;
  focusedMultiValueContainerProps?: Partial<ContainerProps>;
  virtualized?: boolean;
};

const EditStateSelect = ({
  hideHeader,
  Label,
  disabled,
  onFocusWithinChange,
  menuListMaxHeight = '108px',
  ...rest
}: TEditStateSelectProps) => {
  return (
    <EditStateWrapper
      Label={Label}
      hideHeader={hideHeader}
      disabled={disabled}
      onFocusedWithinChange={onFocusWithinChange}
    >
      {({ containerRef, isFocusedWithin, setIsFocusedWithin }) => (
        <EditStateSelectWrapper
          disabled={disabled}
          containerRef={containerRef}
          controlWidth={containerRef?.current?.clientWidth}
          viewableItems="auto"
          menuListMaxHeight={menuListMaxHeight}
          isFocusedWithin={isFocusedWithin}
          setIsFocusedWithin={setIsFocusedWithin}
          {...rest}
        />
      )}
    </EditStateWrapper>
  );
};

export default EditStateSelect;
